import { useRef, useState } from 'react';
import { Field, FieldGroup, FieldLabel, FieldSet, FieldLegend } from '@/components/ui/field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { NewbornGrowthConfiguration, NewbornGrowthReference } from './newbornGrowth';
import { validateNewbornGrowthConfiguration } from './newbornGrowth';
export function parseGrowthRows(contents: string): NewbornGrowthReference['rows'] {
  const lines = contents.trim().split(/\r?\n/);
  const delimiter = lines[0]?.includes('\t') ? '\t' : ',';
  const headers = lines[0].split(delimiter).map((s) => s.trim().toLowerCase());
  const expected = ['sex', 'metric', 'age_basis', 'age_days', 'unit', 'l', 'm', 's'];
  if (headers.join(',') !== expected.join(','))
    throw new Error('Use columns: sex,metric,age_basis,age_days,unit,l,m,s.');
  if (lines.length > 10001) throw new Error('Import at most 10,000 reference rows.');
  const rows = lines
    .slice(1)
    .filter((line) => line.trim())
    .map((line) => {
      const fields = line.split(delimiter).map((s) => s.trim());
      if (fields.length !== 8 || fields.some((v) => !v))
        throw new Error('Each reference row needs eight nonempty fields.');
      if (
        [3, 5, 6, 7].some(
          (i) =>
            !/^[-+]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[-+]?\d+)?$/i.test(fields[i]) || !Number.isFinite(Number(fields[i])),
        )
      )
        throw new Error('Age and LMS parameters must be finite decimal numbers.');
      return {
        sex: fields[0],
        metric: fields[1],
        age_basis: fields[2],
        age_days: Number(fields[3]),
        unit: fields[4],
        l: Number(fields[5]),
        m: Number(fields[6]),
        s: Number(fields[7]),
      } as NewbornGrowthReference['rows'][number];
    });
  const errors = validateNewbornGrowthConfiguration({
    references: [
      {
        id: 'import',
        provider: 'CUSTOM',
        version: 'pending',
        citation: 'Import validation',
        clinically_reviewed: false,
        enabled: false,
        rows,
      },
    ],
  });
  if (errors.length) throw new Error(errors.join(' '));
  return rows;
}
export function NewbornGrowthConfigEditor({
  value,
  onChange,
}: {
  value?: NewbornGrowthConfiguration;
  onChange: (v: NewbornGrowthConfiguration) => void;
}) {
  const loadGeneration = useRef(0);
  const [reading, setReading] = useState(false);
  const [name, setName] = useState(''),
    [provider, setProvider] = useState<'WHO' | 'FENTON' | 'CUSTOM'>('WHO'),
    [version, setVersion] = useState(''),
    [citation, setCitation] = useState(''),
    [rows, setRows] = useState<NewbornGrowthReference['rows']>([]),
    [error, setError] = useState('');
  function add() {
    const ref = {
      id: crypto.randomUUID(),
      provider,
      version,
      citation,
      clinically_reviewed: false,
      enabled: false,
      rows,
    };
    // Reference version identifies the imported source, not the practice publication.
    const next = { references: [...(value?.references ?? []), ref] };
    const issues = validateNewbornGrowthConfiguration(next);
    if (issues.length) {
      setError(issues.join(' '));
      return;
    }
    onChange(next as NewbornGrowthConfiguration);
    setName('');
    setVersion('');
    setCitation('');
    setRows([]);
    setError('');
  }
  return (
    <FieldSet>
      <FieldLegend>Newborn growth references</FieldLegend>
      <p>
        Import source-verified LMS data. No percentiles are calculated until a reference is reviewed and enabled. Rows
        must match sex, measurement, units, and the exact age.
      </p>
      <FieldGroup>
        {(value?.references ?? []).map((ref) => (
          <Field key={ref.id}>
            <FieldLabel>
              {ref.provider} · {ref.version} · {ref.rows.length} rows
            </FieldLabel>
            <p>{ref.citation}</p>
            <Select
              value={ref.enabled ? 'reviewed' : 'off'}
              onValueChange={(v) =>
                onChange({
                  references: value!.references.map((r) =>
                    r.id === ref.id ? { ...r, enabled: v === 'reviewed', clinically_reviewed: v === 'reviewed' } : r,
                  ),
                })
              }>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="reviewed">Reviewed and enabled</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>
        ))}
        <Field>
          <FieldLabel>Source</FieldLabel>
          <Select value={provider} onValueChange={(v) => setProvider(v as typeof provider)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {['WHO', 'FENTON', 'CUSTOM'].map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
        <Field>
          <FieldLabel>Source edition / version</FieldLabel>
          <Input value={version} onChange={(e) => setVersion(e.target.value)} />
        </Field>
        <Field>
          <FieldLabel>Citation and source URL</FieldLabel>
          <Input value={citation} onChange={(e) => setCitation(e.target.value)} />
        </Field>
        <Field>
          <FieldLabel>LMS reference file (CSV or TSV)</FieldLabel>
          <Input
            type="file"
            accept=".csv,.tsv,text/csv,text/tab-separated-values"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const generation = ++loadGeneration.current;
              setReading(true);
              try {
                if (file.size > 2000000) throw new Error('Reference file exceeds 2 MB.');
                const next = parseGrowthRows(await file.text());
                if (generation !== loadGeneration.current) return;
                setRows(next);
                setName(file.name);
                setError('');
              } catch (err) {
                if (generation !== loadGeneration.current) return;
                setRows([]);
                setName('');
                setError(err instanceof Error ? err.message : 'Unable to read file.');
              } finally {
                if (generation === loadGeneration.current) setReading(false);
              }
            }}
          />
          <p>{name ? `${name}: ${rows.length} rows` : 'Columns: sex,metric,age_basis,age_days,unit,l,m,s'}</p>
        </Field>
        {error && <p role="alert">{error}</p>}
        <Button type="button" variant="outline" disabled={reading || !rows.length} onClick={add}>
          Add reference for practice review
        </Button>
      </FieldGroup>
    </FieldSet>
  );
}
